import NextImage from 'next/image';
import cn from 'classnames';

import { HeroUnit as HeroUnitInterface } from '@interfaces';
import { RichTextContent } from '@components/RichTextContent';
import { Button } from '@components/Button';
import { Link } from '@components/Link';
import { useTheme } from '@utilities';
import { Theme } from '@contexts';

import classes from './HeroUnitCenter.classes';

export interface HeroUnitCenterProps extends Omit<HeroUnitInterface, 'centerAligned'> {}

function HeroUnitCenter({
  overlapImage,
  caption,
  captionImage,
  title,
  summary,
  mobileImage,
  desktopImage,
  button,
  link,
  blockTheme,
  blockIdentifier,
}: HeroUnitCenterProps): JSX.Element {
  const theme = useTheme({ blockTheme });

  return (
    <Theme.Provider value={theme}>
      <div
        className={cn(classes.heroUnit, {
          "relative before:content-[''] before:absolute before:bottom-0 before:left-0 before:bg-white before:h-20 md:before:h-24 lg:before:h-40 before:w-full before:rounded-t-3xl before:shadow-[0_0_24px_rgba(0,0,0,0.08)] z-20 pb-8 md:pb-10 lg:pb-12":
            overlapImage,
        })}
      >
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            {caption && <div className={cn(classes.caption, theme.color)}>{caption}</div>}
            {captionImage && (
              <div className={classes.captionImageContainer}>
                <NextImage
                  src={captionImage.src}
                  alt={captionImage.alt || ''}
                  width={captionImage.width}
                  height={captionImage.height}
                  sizes="100vw"
                  className={classes.captionImage}
                />
              </div>
            )}
            {title && <h1 className={classes.title}>{title}</h1>}
            {summary && (
              <div className={classes.summary}>
                <RichTextContent content={summary} />
              </div>
            )}
            {(button || link) && (
              <div className={classes.actionsContainer}>
                {button && (
                  <Button
                    href={button.href}
                    linkType={button.linkType}
                    target={button.target}
                    elementClick={`button[${button.name}]_${blockIdentifier}`}
                  >
                    {button.name}
                  </Button>
                )}
                {link && (
                  <Link
                    href={link.href}
                    linkType={link.linkType}
                    target={link.target}
                    icon="fas fa-arrow-right"
                    elementClick={`link[${link.name}]_${blockIdentifier}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
        {(mobileImage || desktopImage) && (
          <div className={classes.imageContainer}>
            {desktopImage && (
              <div className={classes.desktopImageContainer}>
                <NextImage
                  src={desktopImage.src}
                  alt={desktopImage.src}
                  width={desktopImage.width}
                  height={desktopImage.height}
                  sizes="100vw"
                  quality={75}
                  className={cn(classes.desktopImage, { 'rounded-xl md:rounded-3xl relative': overlapImage })}
                  priority
                />
              </div>
            )}
            {mobileImage && (
              <div className={classes.mobileImageContainer}>
                <NextImage
                  src={mobileImage.src}
                  alt={mobileImage.src}
                  width={mobileImage.width}
                  height={mobileImage.height}
                  sizes="100vw"
                  quality={75}
                  className={cn(classes.mobileImage, { 'rounded-xl relative': overlapImage })}
                  priority
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Theme.Provider>
  );
}

export default HeroUnitCenter;
